import { isNumber } from 'radash';

import type { Diagram } from '../types/Diagram';
import type { DiagramConnection } from '../types/DiagramConnection';
import type { FaultSimulation, FaultSimulationSummary } from '../types/Simulation';

export const J_PER_M2_PER_CAL_PER_CM2 = 41_840;

type FaultResult = FaultSimulation['result'];
type ConnectionLines = FaultResult['connections'][string]['lines'];

export const getFaultCurrentSeries = (lines: ConnectionLines): number[] => {
    const record = lines as unknown as Record<string, { current?: number[] } | undefined>;

    if (Array.isArray(record['L1']?.current) && record['L1']!.current!.length) return record['L1']!.current!;
    if (Array.isArray(record['L+']?.current) && record['L+']!.current!.length) return record['L+']!.current!;
    if (Array.isArray(record['M']?.current) && record['M']!.current!.length) return record['M']!.current!;

    return [];
};

export const getConnectionPeakFaultCurrent = (
    result: FaultResult | null | undefined,
    faultConnectionId: string,
): number | null => {
    const scenarioResult = result?.scenarios?.max ?? result;
    const lines = scenarioResult?.connections?.[faultConnectionId]?.lines;
    if (!lines) return null;

    const currents = getFaultCurrentSeries(lines);
    if (!currents.length) return null;

    return Math.max(...currents.map((value) => Math.abs(value)));
};

export const getFaultTouchVoltage = (
    result: FaultResult | null | undefined,
    connection: DiagramConnection,
): { from?: number; to?: number } | null => {
    const lastSample = (componentInstanceId: string, port: number | null | undefined): number | undefined => {
        if (port === undefined || port === null) return undefined;
        const series = result?.componentInstances?.[componentInstanceId]?.ports?.[port]?.PE?.voltage;
        if (!Array.isArray(series) || series.length === 0) return undefined;
        const last = series[series.length - 1];
        return isNumber(last) ? last : undefined;
    };

    const from = lastSample(connection.from.componentInstanceId, connection.from.port);
    const to = lastSample(connection.to.componentInstanceId, connection.to.port);
    if (from === undefined && to === undefined) return null;
    return { from, to };
};

export const getArcFlashSummary = (
    result: FaultResult | null | undefined,
): { ppeCategory: number; incidentEnergyCalCm2: number } | null => {
    const arcFlash = result?.arcFlash;
    const values = arcFlash?.e_incident?.value;
    if (!arcFlash || !Array.isArray(values) || !values.length) return null;

    const finalJPerM2 = values[values.length - 1] ?? 0;
    return {
        ppeCategory: arcFlash.ppe_category,
        incidentEnergyCalCm2: finalJPerM2 / J_PER_M2_PER_CAL_PER_CM2,
    };
};

export const computeFaultSimulationSummary = ({
    diagram,
    faultConnectionId,
    result,
}: {
    diagram: Pick<Diagram, 'connections'> | null | undefined;
    faultConnectionId: string;
    result: FaultResult | null | undefined;
}): FaultSimulationSummary => {
    if (!result) return { peakFaultCurrent: null };

    const connection = diagram?.connections?.[faultConnectionId];

    return {
        peakFaultCurrent: getConnectionPeakFaultCurrent(result, faultConnectionId),
        arcFlash: getArcFlashSummary(result),
        touchVoltage: connection ? getFaultTouchVoltage(result, connection) : null,
    };
};
